import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Banknote,
  Clock,
  Loader2,
  LockKeyhole,
  UnlockKeyhole,
  X,
} from "lucide-react";

const parseUserRole = (userRole = "") => {
  const [fkuserid = "", roleName = ""] = String(userRole || "").split("|");

  return {
    fkuserid: fkuserid.trim(),
    roleName: roleName.trim(),
  };
};

const formatDrawerDateTime = () => {
  return new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function PosDrawerOperationModal({
  show,
  onClose,
  sessionUser,
  drawerState,
  onSubmit,
  loading = false,
}) {
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [note, setNote] = useState("");
const isDrawerOpen = drawerState?.isOpen === true;

const terminalId = String(sessionUser?.terminalId || "").trim();
const drawerId = String(sessionUser?.drawerId || "").trim();
 

  const { fkuserid, roleName } = useMemo(
    () => parseUserRole(sessionUser?.userRole),
    [sessionUser?.userRole]
  );

  useEffect(() => {
    if (!show) return;

    setAmount("");
    setPin("");

    const actionText = isDrawerOpen ? "closed" : "opened";
    const username = sessionUser?.username || "POS user";
    const dateTime = formatDrawerDateTime();

    setNote(`${actionText} on ${dateTime} by ${username}`);
  }, [show, isDrawerOpen, sessionUser?.username]);

  if (!show) return null;

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d.]/g, "");
    setAmount(value);
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(value);
  };

  const handleSubmit = () => {
    if (!amount || Number(amount) < 0) return;
    if (!pin || pin.length !== 6) return;

   onSubmit({
  amount,
  pin,
  note,
  status: isDrawerOpen ? "CLOSE" : "OPEN",
  terminalId,
  drawerid: drawerId,
});
  };

  const canSubmit =
  amount !== "" &&
  pin.length === 6 &&
  terminalId &&
  drawerId &&
  !loading;

  return (
    <>
   
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-3 py-4 montserrat-font-1">
      <div className="w-full max-w-[500px] max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col">
        {/* Header */}
        <div
          className={`shrink-0 px-5 py-4 border-b ${
            isDrawerOpen
              ? "bg-red-50 border-red-100"
              : "bg-emerald-50 border-emerald-100"
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md ${
                  isDrawerOpen ? "bg-red-500" : "bg-emerald-500"
                }`}
              >
                {isDrawerOpen ? (
                  <LockKeyhole size={22} />
                ) : (
                  <UnlockKeyhole size={22} />
                )}
              </div>

              <div>
                <h2 className="text-base font-bold text-gray-800">
                  {isDrawerOpen ? "Close Cash Drawer" : "Open Cash Drawer"}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {isDrawerOpen
                    ? "Enter closing cash amount to close the drawer session."
                    : "Enter opening cash amount to start drawer session."}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Info Card */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-sm">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <span className="text-gray-500">US Code</span>
              <span className="font-semibold text-gray-800 text-right">
                {sessionUser?.userid || "-"}
              </span>

              <span className="text-gray-500">User ID</span>
              <span className="font-semibold text-gray-800 text-right">
                {fkuserid || "-"}
              </span>

              <span className="text-gray-500">Role</span>
              <span className="font-semibold text-emerald-700 text-right">
                {roleName || "-"}
              </span>

              <span className="text-gray-500">Terminal ID</span>
              <span className="font-semibold text-gray-800 text-right">
                {terminalId || "-"}
              </span>

              <span className="text-gray-500">Drawer ID</span>
              <span className="font-semibold text-gray-800 text-right">
                {drawerId || "-"}
              </span>

              {drawerState?.sessionCode && (
                <>
                  <span className="text-gray-500">Session Code</span>
                  <span className="font-semibold text-blue-700 text-right break-all">
                    {drawerState.sessionCode}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Current Status */}
          <div
            className={`rounded-xl border p-3 flex items-start gap-3 ${
              isDrawerOpen
                ? "bg-red-50 border-red-100 text-red-700"
                : "bg-emerald-50 border-emerald-100 text-emerald-700"
            }`}
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">
                Current drawer status: {isDrawerOpen ? "OPEN" : "CLOSED"}
              </p>
              <p className="text-xs mt-1 opacity-80">
                Drawer status is synced from current drawer balance API.
              </p>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <Banknote size={16} />
              {isDrawerOpen ? "Closing Amount" : "Opening Amount"}
            </label>

            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              placeholder={
                isDrawerOpen
                  ? `Enter Closing Cash Amount`
                  : "Enter opening cash amount"
              }
              disabled={loading}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base font-semibold outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-100"
            />
          </div>

          {/* PIN */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              Drawer PIN
            </label>

            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={handlePinChange}
              placeholder="Enter 6 digit PIN"
              autoComplete="off"
              autofill=""
              disabled={loading}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base font-semibold tracking-[6px] text-center outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-100"
            />
          </div>

          {/* Note */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700 block">
                Note
              </label>

              <span className="text-[11px] text-gray-400 flex items-center gap-1">
                <Clock size={12} />
                Editable
              </span>
            </div>

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={loading}
              rows={2}
              placeholder="Enter drawer note"
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-gray-100 resize-none"
            />
          </div>

          {/* Drawer Balance Details */}
          {drawerState?.isOpen && (
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-sm text-blue-700">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <span>Opening Cash</span>
                <span className="font-bold text-right">
                  ₹{drawerState.openingCash || "0.00"}
                </span>

                <span>Current Balance</span>
                <span className="font-bold text-right">
                  ₹{drawerState.currentBalance || "0.00"}
                </span>

                <span>Cash In</span>
                <span className="font-bold text-right">
                  ₹{drawerState.cashIn || "0.00"}
                </span>

                <span>Cash Out</span>
                <span className="font-bold text-right">
                  ₹{drawerState.cashOut || "0.00"}
                </span>

                <span>Refund</span>
                <span className="font-bold text-right">
                  ₹{drawerState.refund || "0.00"}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-4 border-t bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-6 py-2.5 rounded-xl font-semibold flex items-center gap-2 ${
              canSubmit
                ? isDrawerOpen
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading
              ? "Processing..."
              : isDrawerOpen
              ? "Close Drawer"
              : "Open Drawer"}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}